import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowDown, ArrowUp, Copy, ExternalLink, ImagePlus, Save, Trash2, UserPlus } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SECTIONS } from "@/lib/sections";
import { fmt } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function HubSettings() {
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState({ headline: "", subheadline: "", about: "", hero_image: "", accent_color: "#059669", phone: "", contact_email: "", meta_title: "", meta_description: "", theme: "light", colors: { page_bg: "", text: "" }, gallery: [], why_text: "", services_blurb: "", website: "", socials: { facebook: "", instagram: "", x: "" }, phone_secondary: "", order_lead_time_hours: 24, opening_hours: {}, show_reviews: false, custom_domain: "", travel_enabled: true, base_address: "", travel_rate_per_mile: 1.25, travel_free_miles: 10, show_estimate: true, show_menu_prices: true, name: "", slug: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [integrations, setIntegrations] = useState(null);
  const [integForm, setIntegForm] = useState({ stripe: { secret_key: "", publishable_key: "" }, twilio: { account_sid: "", auth_token: "", from_number: "" } });
  const [galleryUrl, setGalleryUrl] = useState("");
  const [vouchers, setVouchers] = useState([]);
  const [voucherForm, setVoucherForm] = useState({ code: "", kind: "fixed", amount: "" });
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();
  const [staffForm, setStaffForm] = useState({ name: "", email: "", password: "", role: "staff", permissions: ["dashboard"] });
  const [staffLogins, setStaffLogins] = useState([]);
  const [billing, setBilling] = useState(null);
  const [billingError, setBillingError] = useState(false);
  const [plans, setPlans] = useState([]);
  const [planOpen, setPlanOpen] = useState(false);
  const [cal, setCal] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [searchParams] = useSearchParams();

  const loadBilling = () => {
    api.get("/billing/status").then((r) => setBilling(r.data)).catch(() => setBillingError(true));
    api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => {});
  };
  useEffect(() => {
    loadBilling();
    api.get("/calendar/status").then((r) => setCal(r.data)).catch(() => {});
    api.get("/companies/mine/integrations").then((r) => setIntegrations(r.data)).catch(() => {});
    api.get("/vouchers").then((r) => setVouchers(r.data)).catch(() => {});
    api.get("/companies/mine/staff").then((r) => setStaffLogins(r.data)).catch(() => {});
    if (searchParams.get("subscribed")) toast.success("Subscription active — plan updated");
    if (searchParams.get("calendar")) toast.success("Calendar connected — confirmed bookings will sync automatically");
  }, []);

  const load = () => api.get("/companies/mine/info").then((r) => {
    setCompany(r.data);
    setForm({
      headline: r.data.headline || "", subheadline: r.data.subheadline || "", about: r.data.about || "",
      hero_image: r.data.hero_image || "", accent_color: r.data.accent_color || "#059669",
      phone: r.data.phone || "", contact_email: r.data.contact_email || "",
      meta_title: r.data.meta_title || "", meta_description: r.data.meta_description || "",
      theme: r.data.theme || "light",
      colors: { page_bg: r.data.colors?.page_bg || "", text: r.data.colors?.text || "" },
      gallery: r.data.gallery || [],
      why_text: (r.data.why_choose_us || []).join("\n"),
      services_blurb: r.data.services_blurb || "",
      website: r.data.website || "",
      socials: { facebook: r.data.socials?.facebook || "", instagram: r.data.socials?.instagram || "", x: r.data.socials?.x || "" },
      phone_secondary: r.data.phone_secondary || "",
      order_lead_time_hours: r.data.order_lead_time_hours ?? 24,
      opening_hours: r.data.opening_hours || {},
      show_reviews: !!r.data.show_reviews,
      custom_domain: r.data.custom_domain || "",
      travel_enabled: r.data.travel_enabled !== false,
      base_address: r.data.base_address || "",
      travel_rate_per_mile: r.data.travel_rate_per_mile ?? 1.25,
      travel_free_miles: r.data.travel_free_miles ?? 10,
      show_estimate: r.data.show_estimate !== false,
      show_menu_prices: r.data.show_menu_prices !== false,
      name: r.data.name || "",
      slug: r.data.slug || "",
    });
  }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const { why_text, ...rest } = form;
      await api.patch("/companies/mine", { ...rest, why_choose_us: why_text.split("\n").map((l) => l.trim()).filter(Boolean) });
      toast.success("Hub page updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addStaff = async (e) => {
    e.preventDefault();
    try {
      await api.post("/companies/mine/staff", { ...staffForm, permissions: staffForm.role === "staff" ? staffForm.permissions : null });
      toast.success(`Staff login created for ${staffForm.email}`);
      loadStaffLogins();
      setStaffForm({ name: "", email: "", password: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const connectCalendar = async (provider) => {
    try {
      const { data } = await api.get(`/calendar/${provider}/start`, { params: { origin: window.location.origin } });
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const disconnectCalendar = async (provider) => {
    try {
      await api.delete(`/calendar/${provider}`);
      toast.success("Calendar disconnected");
      setCal((c) => ({ ...c, [provider]: { connected: false } }));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadGallery = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/companies/mine/gallery/upload", fd);
      setForm((f) => ({ ...f, gallery: [...f.gallery, data.url] }));
      toast.success("Photo uploaded — Publish changes to make it live");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const addGalleryUrl = () => {
    const url = galleryUrl.trim();
    if (!url) return;
    setForm((f) => ({ ...f, gallery: [...f.gallery, url] }));
    setGalleryUrl("");
  };

  const moveGallery = (i, dir) => setForm((f) => {
    const g = [...f.gallery];
    const j = i + dir;
    [g[i], g[j]] = [g[j], g[i]];
    return { ...f, gallery: g };
  });

  const removeGallery = (i) => setForm((f) => ({ ...f, gallery: f.gallery.filter((_, x) => x !== i) }));

  const saveIntegration = async (provider) => {
    try {
      const { data } = await api.put("/companies/mine/integrations", { [provider]: integForm[provider] });
      setIntegrations(data);
      setIntegForm((f) => ({ ...f, [provider]: provider === "stripe" ? { secret_key: "", publishable_key: "" } : { account_sid: "", auth_token: "", from_number: "" } }));
      toast.success(`${provider === "stripe" ? "Stripe" : "Twilio"} connected`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const disconnectIntegration = async (provider) => {
    try {
      await api.delete(`/companies/mine/integrations/${provider}`);
      setIntegrations((s) => ({ ...s, [provider]: { configured: false } }));
      toast.success("Disconnected");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadStaffLogins = () => api.get("/companies/mine/staff").then((r) => setStaffLogins(r.data)).catch(() => {});

  const updateStaffPerms = async (u, key, on) => {
    const perms = on ? [...(u.permissions || []), key] : (u.permissions || []).filter((p) => p !== key);
    try {
      await api.patch(`/companies/mine/staff/${u.id}`, { permissions: perms });
      setStaffLogins((rows) => rows.map((r) => (r.id === u.id ? { ...r, permissions: perms } : r)));
      toast.success("Permissions updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setStaffRole = async (u, role) => {
    try {
      await api.patch(`/companies/mine/staff/${u.id}`, { role, ...(role === "staff" ? { permissions: u.permissions || [] } : {}) });
      loadStaffLogins();
      toast.success(role === "admin" ? `${u.name} is now an admin` : `${u.name} is now staff`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeStaff = async (u) => {
    try {
      await api.delete(`/companies/mine/staff/${u.id}`);
      loadStaffLogins();
      toast.success("Login removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadVouchers = () => api.get("/vouchers").then((r) => setVouchers(r.data)).catch(() => {});

  const saveVoucher = async () => {
    try {
      await api.post("/vouchers", { ...voucherForm, amount: Number(voucherForm.amount) });
      toast.success("Voucher added");
      setVoucherForm({ code: "", kind: "fixed", amount: "" });
      loadVouchers();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteVoucher = async (v) => {
    try {
      await api.delete(`/vouchers/${v.id}`);
      loadVouchers();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(`<iframe src="${window.location.origin}/embed/hub/${company.slug}" style="width:100%;max-width:640px;height:820px;border:0" title="${company.name} enquiry form"></iframe>`);
      toast.success("Embed snippet copied");
    } catch {
      toast.error("Couldn't copy — your browser blocked clipboard access. Select the snippet text and copy it manually.");
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const subscribe = async (planId) => {
    setSubscribing(true);
    try {
      const { data } = await api.post("/billing/subscribe", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setSubscribing(false);
    }
  };

  if (!company) return <div className="py-20 text-center text-sm text-muted-foreground" data-testid="hub-settings-loading">Loading hub settings…</div>;

  return (
    <div data-testid="hub-settings-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Hub Page CMS</h1>
          <p className="mt-1 text-sm text-muted-foreground">Edit your public business hub — content updates go live instantly.</p>
        </div>
        <Link to={`/hub/${company.slug}`} target="_blank" data-testid="view-hub-link" className="flex items-center gap-1.5 text-sm font-semibold text-brand hover:text-brand-dark">
          View public hub <ExternalLink className="h-4 w-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <form onSubmit={save} className="space-y-4 border border-border bg-white p-6 lg:col-span-2" data-testid="hub-cms-form">
          <div className="space-y-1.5"><Label>Headline</Label><Input data-testid="cms-headline-input" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Subheadline</Label><Input data-testid="cms-subheadline-input" value={form.subheadline} onChange={(e) => setForm({ ...form, subheadline: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>About</Label><Textarea data-testid="cms-about-input" rows={5} value={form.about} onChange={(e) => setForm({ ...form, about: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Hero image URL</Label><Input data-testid="cms-hero-input" value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Accent colour</Label>
              <div className="flex gap-2">
                <input data-testid="cms-color-input" type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="h-9 w-12 cursor-pointer border border-border" />
                <Input value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5"><Label>Public phone</Label><Input data-testid="cms-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Public email</Label><Input data-testid="cms-email-input" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
          </div>
          <div className="border-t border-border pt-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Ordering &amp; opening hours</p>
            <div className="space-y-1.5">
              <Label>Buffet order lead time (hours)</Label>
              <Input data-testid="cms-lead-time-input" type="number" min="0" className="w-32" value={form.order_lead_time_hours} onChange={(e) => setForm({ ...form, order_lead_time_hours: Number(e.target.value) })} />
              <p className="text-xs text-muted-foreground">Customers can't pick a collection date sooner than this. 0 = same-day allowed.</p>
            </div>
            <div className="mt-4 space-y-1.5">
              <Label>Opening hours — if you trade from a fixed location (shown on your hub; order collection times are checked against them)</Label>
              <div className="space-y-1.5" data-testid="opening-hours-editor">
                {[["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]].map(([k, label]) => {
                  const d = form.opening_hours[k] || { open: "", close: "", closed: false };
                  const setDay = (patch) => setForm({ ...form, opening_hours: { ...form.opening_hours, [k]: { ...d, ...patch } } });
                  return (
                    <div key={k} className="flex flex-wrap items-center gap-2 text-sm sm:gap-3" data-testid={`hours-row-${k}`}>
                      <span className="w-10 font-semibold">{label}</span>
                      <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <input type="checkbox" data-testid={`hours-closed-${k}`} checked={!!d.closed} onChange={(e) => setDay({ closed: e.target.checked })} /> Closed
                      </label>
                      <input type="time" data-testid={`hours-open-${k}`} value={d.open || ""} disabled={!!d.closed} onChange={(e) => setDay({ open: e.target.value })} className="min-w-0 flex-1 border border-border px-2 py-1 text-sm disabled:opacity-40 sm:flex-none" />
                      <span className="text-muted-foreground">–</span>
                      <input type="time" data-testid={`hours-close-${k}`} value={d.close || ""} disabled={!!d.closed} onChange={(e) => setDay({ close: e.target.value })} className="min-w-0 flex-1 border border-border px-2 py-1 text-sm disabled:opacity-40 sm:flex-none" />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="border-t border-border pt-4">
            <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" data-testid="show-estimate-toggle" checked={!!form.show_estimate} onChange={(e) => setForm({ ...form, show_estimate: e.target.checked })} /> Show instant price estimate on enquiry</label>
            <p className="mt-1 text-xs text-muted-foreground">Off = customers see "thank you — we'll be in touch with a personalised quote" instead of prices. You still see the estimate in Enquiries.</p>
            <label className="mt-3 flex items-center gap-2 text-sm font-medium"><input type="checkbox" data-testid="menu-prices-toggle" checked={!!form.show_menu_prices} onChange={(e) => setForm({ ...form, show_menu_prices: e.target.checked })} /> Show prices on menu list</label>
            <p className="mt-1 text-xs text-muted-foreground">Off = menus on your hub show dishes only, no per-head prices.</p>
          </div>
          <div className="border-t border-border pt-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Business identity</p>
            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Business name</Label><Input data-testid="identity-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Hub slug</Label>
                <Input data-testid="identity-slug-input" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} />
                <p className="text-xs text-muted-foreground">Changing the slug changes your public hub URL: /hub/{form.slug || "…"}</p>
              </div>
            </div>
          </div>
          <div className="border-t border-border pt-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="max-w-md">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Distance charging</p>
                <p className="mt-0.5 text-xs text-muted-foreground">Round trip = 2 × one-way miles from your base. Charge = 2 × rate × max(0, one-way miles − free miles). Applies to quotes and hub estimates.</p>
              </div>
              <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" data-testid="travel-enabled-toggle" checked={!!form.travel_enabled} onChange={(e) => setForm({ ...form, travel_enabled: e.target.checked })} /> Charge for travel</label>
            </div>
            {form.travel_enabled && (
              <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="travel-config">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Your base location (where units set off from)</Label>
                  <Input data-testid="travel-base-address-input" placeholder="Unit 4, Example Business Park, London E1 6AA" value={form.base_address} onChange={(e) => setForm({ ...form, base_address: e.target.value })} />
                  {company?.base_lat != null && <p className="text-xs font-medium text-emerald-700" data-testid="travel-base-resolved">Base location resolved — distances are measured from here.</p>}
                </div>
                <div className="space-y-1.5">
                  <Label>£ per mile</Label>
                  <Input data-testid="travel-rate-input" type="number" min="0" step="0.01" value={form.travel_rate_per_mile} onChange={(e) => setForm({ ...form, travel_rate_per_mile: Number(e.target.value) })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Free miles (each way)</Label>
                  <Input data-testid="travel-free-miles-input" type="number" min="0" value={form.travel_free_miles} onChange={(e) => setForm({ ...form, travel_free_miles: Number(e.target.value) })} />
                  <p className="text-xs text-muted-foreground">e.g. 50 = the first 50 miles each way are free; only miles above that are billed.</p>
                </div>
              </div>
            )}
          </div>
          <div className="border-t border-border pt-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Colour scheme</p>
            <div className="mb-3 flex gap-2">
              {["light", "warm", "dark"].map((t) => (
                <button key={t} type="button" data-testid={`theme-preset-${t}`} onClick={() => setForm({ ...form, theme: t })}
                  className={`border px-4 py-2 text-sm font-semibold capitalize transition-colors duration-150 ${form.theme === t ? "border-brand bg-emerald-50 text-brand" : "border-border text-muted-foreground hover:bg-muted"}`}>
                  {t}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Page background (optional)</Label>
                <div className="flex gap-2">
                  <input data-testid="cms-page-bg-input" type="color" value={form.colors.page_bg || "#f8fafc"} onChange={(e) => setForm({ ...form, colors: { ...form.colors, page_bg: e.target.value } })} className="h-9 w-12 cursor-pointer border border-border" />
                  <Input placeholder="Theme default" value={form.colors.page_bg} onChange={(e) => setForm({ ...form, colors: { ...form.colors, page_bg: e.target.value } })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Text colour (optional)</Label>
                <div className="flex gap-2">
                  <input data-testid="cms-text-color-input" type="color" value={form.colors.text || "#0f172a"} onChange={(e) => setForm({ ...form, colors: { ...form.colors, text: e.target.value } })} className="h-9 w-12 cursor-pointer border border-border" />
                  <Input placeholder="Theme default" value={form.colors.text} onChange={(e) => setForm({ ...form, colors: { ...form.colors, text: e.target.value } })} />
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-border pt-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Contact &amp; links</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Second phone (optional)</Label><Input data-testid="cms-phone2-input" value={form.phone_secondary} onChange={(e) => setForm({ ...form, phone_secondary: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Website</Label><Input data-testid="cms-website-input" placeholder="https://…" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Facebook</Label><Input data-testid="cms-facebook-input" placeholder="https://facebook.com/…" value={form.socials.facebook} onChange={(e) => setForm({ ...form, socials: { ...form.socials, facebook: e.target.value } })} /></div>
              <div className="space-y-1.5"><Label>Instagram</Label><Input data-testid="cms-instagram-input" placeholder="https://instagram.com/…" value={form.socials.instagram} onChange={(e) => setForm({ ...form, socials: { ...form.socials, instagram: e.target.value } })} /></div>
              <div className="space-y-1.5"><Label>X / Twitter</Label><Input data-testid="cms-x-input" placeholder="https://x.com/…" value={form.socials.x} onChange={(e) => setForm({ ...form, socials: { ...form.socials, x: e.target.value } })} /></div>
              <div className="space-y-1.5"><Label>Custom domain (optional)</Label><Input data-testid="cms-domain-input" placeholder="catering.yourdomain.co.uk" value={form.custom_domain} onChange={(e) => setForm({ ...form, custom_domain: e.target.value })} /></div>
              <div className="flex items-end pb-1"><label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="cms-show-reviews" checked={!!form.show_reviews} onChange={(e) => setForm({ ...form, show_reviews: e.target.checked })} /> Show customer reviews on my hub</label></div>
            </div>
          </div>
          <div className="border-t border-border pt-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Extra sections</p>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Why choose us — one bullet per line</Label><Textarea data-testid="cms-why-input" rows={3} placeholder={"Fully insured &amp; Hygiene rated 5\nOwn water and power — we set up anywhere"} value={form.why_text} onChange={(e) => setForm({ ...form, why_text: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Services &amp; coverage</Label><Textarea data-testid="cms-services-input" rows={3} placeholder="Areas covered, event types, extras…" value={form.services_blurb} onChange={(e) => setForm({ ...form, services_blurb: e.target.value })} /></div>
            </div>
          </div>
          <div className="border-t border-border pt-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">SEO &amp; browser meta</p>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Page title</Label><Input data-testid="cms-meta-title-input" placeholder={`${company.name} — Event Catering`} value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Meta description</Label><Textarea data-testid="cms-meta-desc-input" rows={2} placeholder="Shown in search results and link previews" value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} /></div>
            </div>
          </div>
          <Button data-testid="cms-save-button" type="submit" className="rounded-md bg-ink hover:bg-ink-light"><Save className="mr-2 h-4 w-4" /> Publish changes</Button>
        </form>

        <div className="space-y-4">
          <div className="border border-border bg-white p-6" data-testid="billing-card">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Plan &amp; billing</p>
            {billing ? (
              <div className="mt-3 space-y-3 text-sm">
                {billing.fees_waived ? (
                  <p className="border border-emerald-600 bg-emerald-50 px-3 py-2 font-semibold text-emerald-700" data-testid="fees-waived-badge">Fees waived by platform — full access</p>
                ) : billing.plan ? (
                  <>
                    <div className="flex justify-between"><span className="text-muted-foreground">Plan</span><span className="font-semibold" data-testid="current-plan">{billing.plan.name} — {fmt(billing.plan.price_monthly)}/mo</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Status</span><span className="capitalize">{billing.plan_status}</span></div>
                  </>
                ) : (
                  <p className="text-muted-foreground" data-testid="free-tier-note">Free tier — hub &amp; enquiries only. Subscribe to unlock ordering and the F&amp;B toolkit.</p>
                )}
                {billing.plan_status === "past_due" && !billing.fees_waived && (
                  <div className="space-y-2 border border-amber-400 bg-amber-50 px-3 py-2 text-amber-900" data-testid="past-due-banner">
                    <p className="font-semibold">Payment failed — your {billing.plan?.name || "paid"} features stay on for a 7-day grace period.</p>
                    <Button data-testid="update-payment-button" size="sm" onClick={openPortal} className="rounded-md bg-amber-600 text-white hover:bg-amber-700">Update payment method</Button>
                  </div>
                )}
                {!billing.fees_waived && (
                  <Button data-testid="change-plan-button" size="sm" onClick={() => setPlanOpen(true)} className="rounded-md bg-ink hover:bg-ink-light">
                    {billing.plan ? "Change plan" : "Subscribe"}
                  </Button>
                )}
              </div>
            ) : billingError ? <p className="mt-3 text-sm text-red-600" data-testid="billing-error">Couldn't load billing status — please refresh.</p> : <p className="mt-3 text-sm text-muted-foreground">Loading…</p>}
          </div>

          <div className="border border-border bg-white p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Your hub URL</p>
            <a href={`${window.location.origin}/hub/${company.slug}`} target="_blank" rel="noreferrer" className="mt-2 block break-all font-mono text-sm font-semibold text-brand underline decoration-brand/40 underline-offset-2 hover:text-brand-dark" data-testid="hub-url-display">{window.location.origin}/hub/{company.slug}</a>
            <p className="mt-3 text-xs text-muted-foreground">Share this link — enquiries submitted here land directly in your Enquiries pipeline with an instant estimate.</p>
            <div className="mt-4 space-y-2 border-t border-border pt-4 text-xs">
              <p><span className="font-semibold text-foreground">Buffet pickup ordering:</span> <a href={`${window.location.origin}/hub/${company.slug}/order`} target="_blank" rel="noreferrer" className="font-mono text-brand underline decoration-brand/40 underline-offset-2 hover:text-brand-dark" data-testid="pickup-url-display">{window.location.origin}/hub/{company.slug}/order</a></p>
              <p><span className="font-semibold text-foreground">On-site ordering (QR per table):</span> <a href={`${window.location.origin}/hub/${company.slug}/onsite?t=Table%201`} target="_blank" rel="noreferrer" className="font-mono text-brand underline decoration-brand/40 underline-offset-2 hover:text-brand-dark" data-testid="onsite-url-display">{window.location.origin}/hub/{company.slug}/onsite?t=Table%201</a></p>
            </div>
          </div>

          <div className="border border-border bg-white p-6" data-testid="calendar-sync-card">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Calendar sync</p>
            <p className="mt-1 text-xs text-muted-foreground">Confirmed bookings appear automatically on your connected calendar; cancellations remove them.</p>
            {["google", "microsoft"].map((p) => (
              <div key={p} className="mt-3 flex items-center justify-between border-t border-border pt-3 text-sm" data-testid={`calendar-${p}-row`}>
                <span className="font-medium">{p === "microsoft" ? "Outlook / Microsoft 365" : "Google Calendar"}</span>
                {cal?.[p]?.connected ? (
                  <span className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-emerald-700" data-testid={`calendar-${p}-status`}>Connected{cal[p].account ? ` — ${cal[p].account}` : ""}</span>
                    <Button size="sm" variant="outline" className="rounded-md" data-testid={`calendar-${p}-disconnect`} onClick={() => disconnectCalendar(p)}>Disconnect</Button>
                  </span>
                ) : (
                  <Button size="sm" variant="outline" className="rounded-md" data-testid={`calendar-${p}-connect`} onClick={() => connectCalendar(p)}>Connect</Button>
                )}
              </div>
            ))}
            <p className="mt-3 text-[11px] text-muted-foreground">Needs the platform owner's Google/Microsoft app credentials — if Connect reports "not configured", ask them to add the API keys.</p>
          </div>

          <div className="border border-border bg-white p-6" data-testid="vouchers-card">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Vouchers &amp; promo codes</p>
            <p className="mt-1 text-xs text-muted-foreground">Customers redeem these at checkout on your public order page.</p>
            <div className="mt-3 space-y-2">
              <div className="flex gap-2">
                <Input placeholder="CODE" data-testid="voucher-code-input" value={voucherForm.code} onChange={(e) => setVoucherForm({ ...voucherForm, code: e.target.value.toUpperCase() })} className="w-28 font-mono" />
                <select data-testid="voucher-kind-select" value={voucherForm.kind} onChange={(e) => setVoucherForm({ ...voucherForm, kind: e.target.value })} className="border border-border px-2 text-sm">
                  <option value="fixed">£ off</option>
                  <option value="percent">% off</option>
                </select>
                <Input type="number" min="0" step="0.5" placeholder="Amount" data-testid="voucher-amount-input" value={voucherForm.amount} onChange={(e) => setVoucherForm({ ...voucherForm, amount: e.target.value })} className="w-24" />
              </div>
              <Button type="button" size="sm" data-testid="voucher-add-button" className="rounded-md bg-ink hover:bg-ink-light" onClick={saveVoucher}>Add voucher</Button>
            </div>
            <div className="mt-3 space-y-1.5">
              {vouchers.map((v) => (
                <div key={v.id} className="flex items-center justify-between gap-2 border border-border px-3 py-2 text-sm" data-testid={`voucher-row-${v.id}`}>
                  <span className="font-mono font-bold">{v.code}</span>
                  <span className="flex-1 text-xs text-muted-foreground">{v.kind === "percent" ? `${v.amount}%` : `£${v.amount}`} off · used {v.redeemed_count}{v.max_redemptions ? `/${v.max_redemptions}` : ""}</span>
                  <button type="button" data-testid={`voucher-delete-${v.id}`} onClick={() => deleteVoucher(v)} className="p-1 text-red-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {vouchers.length === 0 && <p className="text-xs text-muted-foreground">No vouchers yet.</p>}
            </div>
          </div>

          <div className="border border-border bg-white p-6" data-testid="gallery-card">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Photo gallery</p>
            <p className="mt-1 text-xs text-muted-foreground">Shown as a grid on your public hub. Upload or paste URLs, then Publish changes.</p>
            <label className="mt-3 flex cursor-pointer items-center justify-center gap-2 border border-dashed border-border px-3 py-2.5 text-sm font-semibold text-ink transition-colors duration-150 hover:bg-muted" data-testid="gallery-upload-label">
              <ImagePlus className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload photo"}
              <input type="file" accept="image/*" className="hidden" data-testid="gallery-upload-input" disabled={uploading} onChange={uploadGallery} />
            </label>
            <div className="mt-2 flex gap-2">
              <Input placeholder="…or paste an image URL" data-testid="gallery-url-input" value={galleryUrl} onChange={(e) => setGalleryUrl(e.target.value)} />
              <Button type="button" size="sm" variant="outline" className="rounded-md" data-testid="gallery-add-url-button" onClick={addGalleryUrl}>Add</Button>
            </div>
            <div className="mt-3 space-y-2">
              {form.gallery.map((url, i) => (
                <div key={i} className="flex items-center gap-2 border border-border p-2" data-testid={`gallery-item-${i}`}>
                  <img src={url} alt="" className="h-10 w-14 shrink-0 object-cover" />
                  <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{url}</span>
                  <button type="button" data-testid={`gallery-up-${i}`} onClick={() => moveGallery(i, -1)} disabled={i === 0} className="p-1 text-muted-foreground hover:text-ink disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button type="button" data-testid={`gallery-down-${i}`} onClick={() => moveGallery(i, 1)} disabled={i === form.gallery.length - 1} className="p-1 text-muted-foreground hover:text-ink disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                  <button type="button" data-testid={`gallery-delete-${i}`} onClick={() => removeGallery(i)} className="p-1 text-red-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {form.gallery.length === 0 && <p className="text-xs text-muted-foreground">No photos yet.</p>}
            </div>
          </div>

          <div className="border border-border bg-white p-6" data-testid="integrations-card">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Integrations — your own accounts</p>
            <div className="mt-3 border-t border-border pt-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-semibold">Stripe</p>
                {integrations?.stripe?.configured && <span className="text-xs font-semibold text-emerald-700" data-testid="stripe-status">Connected ···{integrations.stripe.secret_last4}</span>}
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">Customer payments (deposits, orders, portal) land in your own Stripe account.</p>
              {integrations?.stripe?.configured ? (
                <Button type="button" size="sm" variant="outline" className="mt-2 rounded-md" data-testid="stripe-disconnect" onClick={() => disconnectIntegration("stripe")}>Disconnect</Button>
              ) : (
                <div className="mt-2 space-y-2">
                  <Input type="password" placeholder="Secret key (sk_live_… / sk_test_…)" data-testid="stripe-secret-input" value={integForm.stripe.secret_key} onChange={(e) => setIntegForm({ ...integForm, stripe: { ...integForm.stripe, secret_key: e.target.value } })} />
                  <Input placeholder="Publishable key (pk_…)" data-testid="stripe-publishable-input" value={integForm.stripe.publishable_key} onChange={(e) => setIntegForm({ ...integForm, stripe: { ...integForm.stripe, publishable_key: e.target.value } })} />
                  <Button type="button" size="sm" className="rounded-md bg-ink hover:bg-ink-light" data-testid="stripe-save" onClick={() => saveIntegration("stripe")}>Connect Stripe</Button>
                </div>
              )}
            </div>
            <div className="mt-4 border-t border-border pt-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-semibold">Twilio SMS</p>
                {integrations?.twilio?.configured && <span className="text-xs font-semibold text-emerald-700" data-testid="twilio-status">Connected · {integrations.twilio.from_number}</span>}
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">SMS dispatch for provider RFQs and customer notifications.</p>
              {integrations?.twilio?.configured ? (
                <Button type="button" size="sm" variant="outline" className="mt-2 rounded-md" data-testid="twilio-disconnect" onClick={() => disconnectIntegration("twilio")}>Disconnect</Button>
              ) : (
                <div className="mt-2 space-y-2">
                  <Input placeholder="Account SID (AC…)" data-testid="twilio-sid-input" value={integForm.twilio.account_sid} onChange={(e) => setIntegForm({ ...integForm, twilio: { ...integForm.twilio, account_sid: e.target.value } })} />
                  <Input type="password" placeholder="Auth token" data-testid="twilio-token-input" value={integForm.twilio.auth_token} onChange={(e) => setIntegForm({ ...integForm, twilio: { ...integForm.twilio, auth_token: e.target.value } })} />
                  <Input placeholder="From number (+44…)" data-testid="twilio-from-input" value={integForm.twilio.from_number} onChange={(e) => setIntegForm({ ...integForm, twilio: { ...integForm.twilio, from_number: e.target.value } })} />
                  <Button type="button" size="sm" className="rounded-md bg-ink hover:bg-ink-light" data-testid="twilio-save" onClick={() => saveIntegration("twilio")}>Connect Twilio</Button>
                </div>
              )}
            </div>
          </div>

          {company?.has_demo_data && (
            <div className="border border-amber-300 bg-amber-50 p-6" data-testid="demo-data-card">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-800">Demo data active</p>
              <p className="mt-1 text-sm text-amber-900">Your trial came pre-filled with sample venues, menus, a booking and an enquiry so you can explore. Ready to go live? Remove it all in one click — your own data is never touched.</p>
              <Button type="button" data-testid="remove-demo-button" variant="outline" className="mt-3 rounded-md border-amber-600 text-amber-800 hover:bg-amber-100"
                onClick={async () => {
                  if (!window.confirm("Remove ALL demo data (venues, menus, demo booking etc.)? This cannot be undone.")) return;
                  try {
                    const r = await api.post("/companies/mine/remove-demo-data");
                    toast.success(`Demo data removed (${r.data.removed} records) — you're live`);
                    load();
                  } catch (err) {
                    toast.error(formatApiError(err));
                  }
                }}>
                Remove demo data & go live
              </Button>
            </div>
          )}
          <div className="border border-border bg-white p-6" data-testid="password-card">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Your password</p>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                await api.post("/auth/change-password", pwForm);
                toast.success("Password changed");
                setPwForm({ current_password: "", new_password: "" });
              } catch (err) {
                toast.error(formatApiError(err));
              }
            }} className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Current password</Label><Input data-testid="pw-current-input" type="password" required value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>New password (min 8 chars)</Label><Input data-testid="pw-new-input" type="password" required minLength={8} value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} /></div>
              <div className="sm:col-span-2"><Button data-testid="pw-change-button" type="submit" className="rounded-md bg-ink hover:bg-ink-light">Change password</Button></div>
            </form>
          </div>
          <div className="border border-border bg-white p-6" data-testid="embed-card">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Embed enquiry form</p>
            <p className="mt-1 text-xs text-muted-foreground">Drop this snippet into your own website to take enquiries directly.</p>
            <pre className="mt-3 overflow-x-auto bg-muted p-3 text-[11px]" data-testid="embed-snippet">{`<iframe src="${window.location.origin}/embed/hub/${company.slug}" style="width:100%;max-width:640px;height:820px;border:0" title="${company.name} enquiry form"></iframe>`}</pre>
            <div className="mt-2 flex items-center gap-3">
              <Button type="button" size="sm" variant="outline" className="rounded-md" data-testid="embed-copy-button" onClick={copyEmbed}><Copy className="mr-1 h-3.5 w-3.5" /> Copy snippet</Button>
              <Link to={`/embed/hub/${company.slug}`} target="_blank" data-testid="embed-preview-link" className="flex items-center gap-1 text-sm font-semibold text-brand hover:text-brand-dark">Preview <ExternalLink className="h-3.5 w-3.5" /></Link>
            </div>
          </div>

          <form onSubmit={addStaff} className="space-y-4 border border-border bg-white p-6" data-testid="add-staff-login-form">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Logins &amp; access</p>
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="staff-login-name" required value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="staff-login-email" type="email" required value={staffForm.email} onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Password</Label><Input data-testid="staff-login-password" required minLength={8} value={staffForm.password} onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <select data-testid="staff-role-select" value={staffForm.role} onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })} className="w-full border border-border bg-white px-3 py-2 text-sm">
                <option value="staff">Staff — restricted access</option>
                <option value="admin">Admin — full access</option>
              </select>
            </div>
            {staffForm.role === "staff" && (
              <div className="space-y-1.5" data-testid="staff-permissions-editor">
                <Label>What they can access</Label>
                <div className="grid grid-cols-2 gap-1">
                  {SECTIONS.map(([key, label]) => (
                    <label key={key} className="flex items-center gap-1.5 text-xs">
                      <input type="checkbox" data-testid={`perm-${key}`} checked={staffForm.permissions.includes(key)}
                        onChange={(e) => setStaffForm({ ...staffForm, permissions: e.target.checked ? [...staffForm.permissions, key] : staffForm.permissions.filter((p) => p !== key) })} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <Button data-testid="staff-login-submit" type="submit" variant="outline" className="w-full rounded-md border-ink text-ink hover:bg-muted">
              <UserPlus className="mr-2 h-4 w-4" /> Create login
            </Button>
            {staffLogins.length > 0 && (
              <div className="space-y-2 border-t border-border pt-3" data-testid="staff-logins-list">
                {staffLogins.map((u) => (
                  <div key={u.id} className="border border-border p-2.5 text-sm" data-testid={`staff-login-row-${u.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <span className="font-medium">{u.name} <span className="text-xs text-muted-foreground">({u.email})</span></span>
                      <span className="text-xs font-semibold capitalize">{u.role}</span>
                    </div>
                    {u.role !== "admin" && (
                      <div className="mt-2 grid grid-cols-2 gap-1">
                        {SECTIONS.map(([key, label]) => (
                          <label key={key} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <input type="checkbox" data-testid={`perm-${u.id}-${key}`} checked={(u.permissions || []).includes(key)}
                              onChange={(e) => updateStaffPerms(u, key, e.target.checked)} />
                            {label}
                          </label>
                        ))}
                      </div>
                    )}
                    <div className="mt-2 flex gap-2">
                      {u.role !== "admin" && <Button type="button" size="sm" variant="outline" className="rounded-md text-xs" data-testid={`staff-make-admin-${u.id}`} onClick={() => setStaffRole(u, "admin")}>Make admin</Button>}
                      {u.role === "admin" && u.email !== user?.email && <Button type="button" size="sm" variant="outline" className="rounded-md text-xs" data-testid={`staff-make-staff-${u.id}`} onClick={() => setStaffRole(u, "staff")}>Make staff</Button>}
                      {u.email !== user?.email && <Button type="button" size="sm" variant="outline" className="rounded-md text-xs text-red-600" data-testid={`staff-remove-${u.id}`} onClick={() => removeStaff(u)}>Remove</Button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </form>
        </div>
      </div>
      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="plans-dialog">
          <DialogHeader><DialogTitle className="font-heading">Choose a plan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {plans.map((p) => (
              <button key={p.id} data-testid={`plan-option-${p.id}`} onClick={() => subscribe(p.id)} disabled={subscribing}
                className="w-full border border-border p-4 text-left transition-colors duration-150 hover:bg-slate-50">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="font-heading font-bold">{p.name}</span>
                  <span className="font-heading font-black text-brand tabular-nums">{fmt(p.price_monthly)}/mo</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{p.description}</p>
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Monthly subscription via Stripe (test mode). Cancel any time via the platform team.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
